import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

const stripePromise = loadStripe("pk_live_51QhCfzBr8IQkrP17SCUHe7q5xESuGwYj0IqvziLBe4wBNDZIBQLPZb3IZleGCiOex629oewFQ0W7JUPYyBQUoXVW00NS6qpn81");

const BillingAndPayment = ({ onNext, onBack, formData, carDetails }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTerms, setShowTerms] = useState(false); // State to toggle modal
    const [errorMessage, setErrorMessage] = useState("");

    const taxes = 0.135; // 13.5% tax
    const deposit = 80; // Non-refundable deposit
    const { rentalDays = 0, pricePerDay = 0, total = 0 } = formData;

    const rentalSubtotal = pricePerDay * rentalDays;
    const taxAmount = rentalSubtotal * taxes;
    const totalWithTax = rentalSubtotal + taxAmount;
    const balanceDue = totalWithTax - deposit;

    const navigate = useNavigate();

    useEffect(() => {
        if (total !== totalWithTax) {
            onNext({ total: totalWithTax });
        }
    }, [total, totalWithTax, onNext]);

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (!stripe || !elements) {
            console.error("Stripe.js not loaded properly");
            return;
        }
    
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: undefined, // Ensure no automatic redirection
                },
                redirect: 'if_required', // Prevent redirection and handle it manually
            });
    
            if (error) {
                setErrorMessage(error.message);
                console.error("Error confirming payment:", error.message);
                return;
            }
    
            if (paymentIntent && paymentIntent.status === "succeeded") {
                console.log("Payment confirmed. Proceeding to add booking...");
    
                // Proceed to call the backend API to add booking
                const bookingData = {
                    car_id: carDetails?.id || formData.carId,
                    customer_name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    start_date: formData.startDate,
                    end_date: formData.endDate,
                    total_price: totalWithTax,
                    license_url: `${formData.frontLicenseFile},${formData.backLicenseFile}`,
                };
    
                const response = await fetch(`https://api.karrentals.ca/add-booking`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(bookingData),
                });
    
                const data = await response.json();
                if (response.ok && data.success) {
                    navigate("/confirmation", {
                        state: {
                            bookingId: data.bookingId,
                            carDetails,
                            ...formData,
                            total_price: totalWithTax,
                        },
                    });
                } else {
                    console.error("Failed to add booking:", data.error);
                    setErrorMessage("Failed to create booking after payment. Please contact support.");
                }
            } else {
                console.error("Payment failed or was not successful:", paymentIntent?.status);
                setErrorMessage("Payment failed or was not successful. Please try again.");
            }
        } catch (error) {
            console.error("Error during payment and booking:", error.message);
            setErrorMessage("An unexpected error occurred.");
        }
    };           

    return (
        <div>
            <h2 className="text-2xl font-bold mb-4">Billing & Payment</h2>
            <div className="bg-gray-100 p-4 rounded mb-4">
                <h3 className="text-xl font-bold mb-2">Summary</h3>
                <ul className="text-gray-700">
                    <li className="flex justify-between">
                        <span>Rental:</span>
                        <span>
                            {rentalDays} × ${pricePerDay.toFixed(2)}
                        </span>
                    </li>
                    <li className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>${rentalSubtotal.toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between">
                        <span className="flex items-center">
                            Taxes (13.5%)
                            <Tooltip
                                title={
                                    <>
                                        <div><strong>Breakdown:</strong></div>
                                        <div>5% GST (Federal)</div>
                                        <div>7% PST (Provincial)</div>
                                        <div>1.5% MRDT (Regional)</div>
                                    </>
                                }
                                arrow
                            >
                                <span className="ml-2 text-gray-500 cursor-pointer">
                                    &#9432;
                                </span>
                            </Tooltip>
                        </span>
                        <span>${taxAmount.toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between font-bold text-black">
                        <span>Total with Tax:</span>
                        <span>${totalWithTax.toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between font-bold text-blue-500">
                        <span>Due Now (Non-refundable Deposit):</span>
                        <span>${deposit.toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between font-bold text-red-500">
                        <span>Balance Due at Pickup:</span>
                        <span>${balanceDue.toFixed(2)}</span>
                    </li>
                </ul>
            </div>
            <div className="space-y-4">
                {/* Render Stripe PaymentElement */}
                <form onSubmit={handleSubmit}>
                    <PaymentElement />
                    <div className="mt-4">
                        <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="mr-2"
                        />
                        I agree to the Terms and Conditions.
                        </label>
                    </div>
                    {errorMessage && <div className="text-red-500">{errorMessage}</div>}
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-500 text-white rounded w-full"
                        disabled={!stripe || !termsAccepted}
                    >
                        Pay ${deposit.toFixed(2)}
                    </button>
                </form>
            </div>
            <div className="flex justify-between mt-6">
                <button onClick={onBack} className="px-4 py-2 bg-gray-300 text-black rounded">
                    Back
                </button>
            </div>

            {/* Terms and Conditions Modal */}
            {showTerms && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-lg max-w-lg w-full">
                        <h3 className="text-xl font-bold mb-4">Terms and Conditions</h3>
                        <div className="text-gray-700 space-y-4">
                            <p>
                                By proceeding with this booking, you agree to the following terms:
                            </p>
                            <ul className="list-disc list-inside">
                                <li>All deposits are non-refundable.</li>
                                <li>Bookings can be modified or canceled up to 24 hours before pickup.</li>
                                <li>Balance payments must be made at pickup.</li>
                                <li>Valid driver's license is required at pickup, with images uploaded during booking.</li>
                                <li>Rental includes insurance coverage; USA travelers may use their personal rental car insurance if applicable.</li>
                                <li>Rental includes 200 km per day. Additional mileage will incur charges.</li>
                                <li>Smoking or vaping in the vehicle is strictly prohibited and will result in cleaning fees.</li>
                                <li>Pets over 20 lbs are not allowed; smaller pets are permitted if the car is returned clean.</li>
                                <li>Tolls, tickets, and towing charges incurred during the rental will be charged to the credit card on file.</li>
                                <li>Cross-border and inter-province travel are not allowed without prior approval.</li>
                            </ul>
                            <p>For more details, please refer to the full Terms and Conditions on our website.</p>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => setShowTerms(false)}
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const BillingPaymentStepWrapper = (props) => {
    const [clientSecret, setClientSecret] = useState(null);

    useEffect(() => {
        const depositInCents = 8000; // Replace with dynamic calculation if needed
        fetch("https://api.karrentals.ca/create-payment-intent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount: depositInCents }),
        })
            .then((res) => res.json())
            .then((data) => {
                console.log("Client secret from wrapper:", data.clientSecret);
                setClientSecret(data.clientSecret);
            })
            .catch((err) => console.error("Failed to fetch client secret:", err));
    }, []);

    if (!clientSecret) {
        return <div>Loading payment options...</div>;
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
            <BillingAndPayment {...props} />
        </Elements>
    );
};

export default BillingPaymentStepWrapper;